import { useCallback, useMemo, useState, useEffect } from "react"

import type { ITableColumnsType } from "../types"
import { getColumnKey } from "../utils/column-key"
import { useTableContext } from "../contexts/table-context"

interface UseColumnResizeReturn<T = any> {
    columns: ITableColumnsType<T>
}

const DEFAULT_MIN_WIDTH = 100
const DEFAULT_MAX_WIDTH = 500
const DEFAULT_WIDTH = 150

export const useColumnResize = <T>(initialColumns: ITableColumnsType<T>, columnOrder?: string[]): UseColumnResizeReturn<T> => {
    const { preferences } = useTableContext()

    const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number>>({})

    const savedColumnWidths = useMemo(() => {
        return preferences?.state.columnWidths || {}
    }, [preferences?.state.columnWidths])

    // Reset local column widths when preferences are reset
    useEffect(() => {
        const savedWidths = preferences?.state.columnWidths
        if (preferences && savedWidths && Object.keys(savedWidths).length === 0) {
            // Preferences were reset, clear local column widths
            setLocalColumnWidths({})
        }
    }, [preferences?.state.columnWidths, preferences])

    // Merge saved preferences with local changes
    const columnWidths = useMemo(() => {
        return { ...savedColumnWidths, ...localColumnWidths }
    }, [savedColumnWidths, localColumnWidths])

    const orderedColumns = useMemo(() => {
        if (!columnOrder) return initialColumns

        const columnMap = new Map(
            initialColumns.map((col, index) => {
                const key = getColumnKey(col, index)
                return [key, col]
            })
        )

        return columnOrder.map((key) => columnMap.get(key)).filter(Boolean) as ITableColumnsType<T>
    }, [initialColumns, columnOrder])

    const handleResize = useCallback(
        (index: number) => {
            return (_e: React.SyntheticEvent, data: { size: { width: number } }) => {
                const column = orderedColumns[index]
                if (!column) return

                const { width } = data.size
                const minWidth = column.minWidth || DEFAULT_MIN_WIDTH
                const maxWidth = column.maxWidth || DEFAULT_MAX_WIDTH
                const clampedWidth = Math.min(Math.max(width, minWidth), maxWidth)

                const columnKey = column.key as string
                setLocalColumnWidths((prev) => ({
                    ...prev,
                    [columnKey]: clampedWidth
                }))

                // Update preferences directly
                if (preferences) {
                    preferences.setColumnWidth(columnKey, clampedWidth)
                }
            }
        },
        [orderedColumns, preferences]
    )

    const columns = useMemo(() => {
        return orderedColumns.map((col, index) => {
            const columnKey = getColumnKey(col, index)
            const storedWidth = columnWidths[columnKey]
            const effectiveWidth = storedWidth || (typeof col.width === "number" ? col.width : DEFAULT_WIDTH)

            return {
                ...col,
                width: effectiveWidth,
                showSorterTooltip: false,
                onHeaderCell: () => ({
                    id: columnKey,
                    width: effectiveWidth,
                    onResize: handleResize(index),
                    dataIndex: col.dataIndex
                }),
                key: columnKey,
                onCell: () => ({ id: columnKey })
            }
        }) as ITableColumnsType<T>
    }, [orderedColumns, columnWidths, handleResize])

    return {
        columns
    }
}
